import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'wouter';
import { api } from '../lib/api';
import {
  dateTime,
  deadlineLabels,
  deadlineTone,
  money,
  paymentClaimLabels,
  paymentClaimTone,
  shortDate,
  stageLabels,
} from '../lib/format';
import { haptic } from '../lib/telegram';
import type { CustomerOrderDetails } from '../types';
import { downloadAttachment } from '../lib/download';
import { orderMilestones, revisionsLeft } from '../lib/order-milestones';
import { ClosedOrderContact } from '../components/closed-order-contact';
import { DocumentTile, isDocumentAttachment } from '../components/order-files';
import { OrderReferences } from '../components/order-references';
import { PaymentClaimForm } from '../components/payment-claim-form';
import { ZoomableImage } from '../components/zoomable-image';
import { CommentThread } from '../components/comment-thread';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  LoadingScreen,
  Modal,
  PageHeader,
  Select,
} from '../components/ui';

type DialogKind = 'comment' | 'revision' | 'approve' | null;

export function CustomerOrderPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = async (url: string, fileName: string) => {
    setDownloadError(null);
    try {
      await downloadAttachment(url, fileName);
    } catch {
      setDownloadError('Не удалось скачать файл');
    }
  };
  const query = useQuery({
    queryKey: ['customer', 'order', id],
    queryFn: () => api<CustomerOrderDetails>(`/customer/orders/${id}`),
  });

  const invalidate = (updated?: CustomerOrderDetails) => {
    haptic('success');
    if (updated) queryClient.setQueryData(['customer', 'order', id], updated);
    void queryClient.invalidateQueries({ queryKey: ['customer', 'orders'] });
    void queryClient.invalidateQueries({ queryKey: ['customer', 'overview'] });
  };

  const approveSketch = useMutation({
    mutationFn: () =>
      api<CustomerOrderDetails>(`/customer/orders/${id}/approve-sketch`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (updated) => invalidate(updated),
  });
  const confirmDelivery = useMutation({
    mutationFn: () =>
      api<CustomerOrderDetails>(`/customer/orders/${id}/confirm-delivery`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (updated) => invalidate(updated),
  });
  const agreeDeadline = useMutation({
    mutationFn: () =>
      api<CustomerOrderDetails>(`/customer/orders/${id}/agree-deadline`, { method: 'POST' }),
    onSuccess: (updated) => invalidate(updated),
  });
  const showcase = useMutation({
    mutationFn: (value: CustomerOrderDetails['showcase']) =>
      api<CustomerOrderDetails>(`/customer/orders/${id}/showcase`, {
        method: 'POST',
        body: { showcase: value },
      }),
    onSuccess: (updated) => invalidate(updated),
  });

  if (query.isPending) return <LoadingScreen label="Открываем заказ" />;
  if (query.error) return <ErrorNotice error={query.error} />;
  const order = query.data;
  const revisions = revisionsLeft(order);
  const closed = order.status === 'COMPLETED' || order.status === 'CANCELED';

  return (
    <>
      {/* Стрелка «назад» живёт в шапке приложения, здесь она была бы второй. */}
      <PageHeader eyebrow={order.publicNumber} title={order.title} />

      <div className="order-hero">
        <p>{order.artist.displayName}</p>
      </div>

      {/*
        Пять вех вместо пятнадцати этапов: этапы — рабочая кухня художника, а
        заказчику нужен ответ на вопрос «что уже случилось и чего ждут от меня».
      */}
      <Card className="stack stack--compact">
        <ol className="milestones" aria-label="Ход работы">
          {orderMilestones(order).map((milestone) => (
            <li key={milestone.key} className={`milestone is-${milestone.state}`}>
              <span className="milestone__dot" aria-hidden="true">
                {milestone.state === 'done' ? '✓' : ''}
              </span>
              <span className="milestone__label">{milestone.label}</span>
            </li>
          ))}
        </ol>

        <div className="fact-row">
          <span className="meta">Ожидается</span>
          <span className={`meta text-${deadlineTone(order.deadlineState)}`}>
            {shortDate(order.deadline)} · {deadlineLabels[order.deadlineState].toLowerCase()}
          </span>
        </div>
        {order.showPrice && order.payment && (
          <div className="fact-row">
            <span className="meta">Оплачено</span>
            <span className="meta">
              {money(order.payment.paidAmount, order.currency)} из{' '}
              {money(order.totalPrice!, order.currency)}
            </span>
          </div>
        )}
        {/* Очередь объясняет срок: художник берёт заказы по порядку. */}
        {order.queuePosition !== null && order.group !== 'completed' && (
          <div className="fact-row">
            <span className="meta">В очереди</span>
            <span className="meta">№ {order.queuePosition} · художник берёт заказы по порядку</span>
          </div>
        )}
        <span className="meta">Обновлено {dateTime(order.stageUpdatedAt)}</span>
      </Card>

      {/*
        Срок предлагает заказчик в заявке, а назначает художник — он один знает
        свою загрузку. Здесь заказчик видит назначенную дату и принимает её.
        Пока не принял, работа не стоит: это согласие, а не блокировка.
      */}
      {order.canAgreeDeadline && (
        <>
          <div className="section-heading">
            <h2>Срок работы</h2>
          </div>
          <Card className="stack stack--compact">
            <p>
              Художник назначил срок <strong>{shortDate(order.deadline)}</strong>. Подтвердите, что
              он вам подходит.
            </p>
            <p className="muted settings-note">
              Не подходит — напишите художнику, он предложит другую дату.
            </p>
            {agreeDeadline.error && <ErrorNotice error={agreeDeadline.error} />}
            <Button disabled={agreeDeadline.isPending} onClick={() => agreeDeadline.mutate()}>
              {agreeDeadline.isPending ? 'Подтверждаем…' : 'Срок подходит'}
            </Button>
          </Card>
        </>
      )}

      {(order.canApproveSketch || order.canConfirmDelivery || order.canRequestRevision) && (
        <>
          <div className="section-heading">
            <h2>Ваши действия</h2>
          </div>

          {/*
            Счётчик правок стоит до того, как заказчик попросит правку, а не
            после: увиденный вовремя, он удерживает объём работы честным, а
            увиденный постфактум — просто расстраивает.
          */}
          {order.canRequestRevision && (
            <Card className="stack stack--compact">
              <div className="fact-row">
                <span className="meta">Бесплатные правки</span>
                <strong>
                  {revisions.left} из {revisions.limit}
                </strong>
              </div>
              <p className="muted settings-note">
                Дальше правки обсуждаются с художником отдельно.
              </p>
            </Card>
          )}

          <div className="status-actions">
            {order.canApproveSketch && (
              <Button disabled={approveSketch.isPending} onClick={() => setDialog('approve')}>
                Согласовать скетч
              </Button>
            )}
            {order.canRequestRevision && (
              <Button variant="secondary" onClick={() => setDialog('revision')}>
                Запросить правки
              </Button>
            )}
            {order.canConfirmDelivery && (
              <Button disabled={confirmDelivery.isPending} onClick={() => confirmDelivery.mutate()}>
                Подтвердить получение
              </Button>
            )}
          </div>
          {approveSketch.error && <ErrorNotice error={approveSketch.error} />}
          {confirmDelivery.error && <ErrorNotice error={confirmDelivery.error} />}
        </>
      )}

      {/* Готовая работа попадает в профиль художника только с согласия
          заказчика — и имя показывается лишь отдельным выбором. */}
      {order.canShowcase && (
        <>
          <div className="section-heading">
            <h2>Показ в профиле художника</h2>
          </div>
          <Card className="stack">
            <Field label="Кто увидит эту работу">
              <Select
                value={order.showcase}
                disabled={showcase.isPending}
                onChange={(event) =>
                  showcase.mutate(event.target.value as CustomerOrderDetails['showcase'])
                }
              >
                <option value="HIDDEN">Никто — работа скрыта</option>
                <option value="ANONYMOUS">Все, без моего имени</option>
                <option value="CREDITED">Все, с моим ником</option>
              </Select>
            </Field>
            <p className="muted settings-note">В профиле появятся только название работы и теги.</p>
            {showcase.error && <ErrorNotice error={showcase.error} />}
          </Card>
        </>
      )}

      {order.showPrice && order.payment && (
        <Card className="payment-card">
          <div className="card-row">
            <div>
              <p className="eyebrow">Оплачено</p>
              <h2>{money(order.payment.paidAmount, order.currency)}</h2>
            </div>
          </div>
          <div className="card-row">
            <span>Итого {money(order.totalPrice ?? '0', order.currency)}</span>
            <strong>Остаток {money(order.payment.balanceDue, order.currency)}</strong>
          </div>
        </Card>
      )}

      <div className="section-heading">
        <h2>О заказе</h2>
      </div>
      <Card className="stack stack--compact">
        <div className="prose-block">
          <span>Описание</span>
          <p>{order.description}</p>
        </div>
        <div className="detail-grid">
          <div>
            <span>Услуга</span>
            <strong>{order.service?.title ?? 'Без услуги'}</strong>
          </div>
          <div>
            <span>Правки</span>
            <strong>
              {order.revisionUsed} из {order.revisionLimit}
            </strong>
          </div>
        </div>
        {order.clientPublicNote && (
          <div className="prose-block">
            <span>Комментарий художника</span>
            <p>{order.clientPublicNote}</p>
          </div>
        )}
      </Card>

      <div className="section-heading">
        <h2>Переписка</h2>
        {!closed && (
          <Button variant="ghost" onClick={() => setDialog('comment')}>
            Написать
          </Button>
        )}
      </div>
      <Card className="stack stack--compact">
        <CommentThread
          comments={order.comments}
          emptyText={
            closed
              ? 'Переписки по этому заказу не было.'
              : 'Пока тихо. Напишите художнику отсюда или ответьте в чате бота командой /chat.'
          }
          counterpartName={order.artist.displayName}
        />
        {/*
          По закрытому заказу переписки нет с обеих сторон: ответить здесь уже
          нельзя, поэтому вместо неработающей формы показываем, куда писать.
        */}
        {closed && <ClosedOrderContact email={order.artist.contactEmail} who="Художник" />}
      </Card>

      <OrderReferences order={order} />

      {/*
        Оплату заказчик переводит сам, мимо приложения. Здесь он говорит, что
        перевёл, и прикладывает чек — а поступление подтверждает художник по
        своему счёту. Раньше о переводе можно было только написать в чате.
      */}
      <div className="section-heading">
        <h2>Оплата</h2>
      </div>
      {order.canReportPayment ? (
        <PaymentClaimForm order={order} />
      ) : (
        <Card>
          <p className="muted">
            {closed
              ? 'Заказ закрыт: сообщить об оплате по нему уже нельзя.'
              : 'Ваше сообщение об оплате ждёт художника — он сверится со счётом и ответит.'}
          </p>
        </Card>
      )}

      {order.paymentClaims.length > 0 && (
        <Card className="stack stack--compact">
          {order.paymentClaims.map((claim) => (
            <div key={claim.id} className="card-row claim claim--resolved">
              <div>
                <strong>
                  {claim.confirmedAmount ?? claim.amount} {claim.currency}
                </strong>
                {/* Сумму могли уточнить — заказчик должен видеть, какую зачли. */}
                {claim.confirmedAmount && claim.confirmedAmount !== claim.amount && (
                  <p className="meta">Вы указывали {claim.amount}</p>
                )}
                {claim.resolutionNote && <p className="meta">{claim.resolutionNote}</p>}
                <p className="meta">{dateTime(claim.createdAt)}</p>
              </div>
              <span className={`meta ${paymentClaimTone(claim.status)}`}>
                {paymentClaimLabels[claim.status]}
              </span>
            </div>
          ))}
        </Card>
      )}

      {/* Файлы работы: заказчик видит только то, что художник открыл ему. */}
      {order.attachments.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Файлы</h2>
            <span className="meta">{order.attachments.length}</span>
          </div>
          <Card>
            {downloadError && <span className="field__error">{downloadError}</span>}
            <div className="file-grid">
              {order.attachments.map((file) => (
                <figure key={file.id} className="file-tile">
                  {file.url && !isDocumentAttachment(file.mimeType) ? (
                    <ZoomableImage url={file.url} alt={file.fileName} />
                  ) : (
                    <DocumentTile fileName={file.fileName} sizeBytes={file.sizeBytes} />
                  )}
                  <figcaption>
                    <span className="meta">{shortDate(file.createdAt)}</span>
                    {file.downloadUrl && (
                      <div className="file-tile__actions">
                        <button
                          type="button"
                          onClick={() => void download(file.downloadUrl!, file.fileName)}
                        >
                          Скачать
                        </button>
                      </div>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Card>
        </>
      )}

      <div className="section-heading">
        <h2>Этапы работы</h2>
      </div>
      <Card>
        <div className="timeline">
          {[...order.stageHistory].reverse().map((entry) => (
            <div className="timeline__item" key={entry.id}>
              <span className="timeline__dot" />
              <div>
                <strong>{stageLabels[entry.newStage]}</strong>
                <p>{dateTime(entry.createdAt)}</p>
                {entry.publicComment && <small>{entry.publicComment}</small>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/*
        Согласование — необратимое действие: после него композиция закреплена,
        и всё, что заказчик захочет поменять дальше, пойдёт в счёт правок.
        Поэтому кнопка не срабатывает сразу, а сначала пересказывает, что
        именно закрепляется.
      */}
      {dialog === 'approve' && (
        <Modal title="Согласовать скетч" onClose={() => setDialog(null)}>
          <div className="stack stack--compact">
            <p>Композиция, поза и кадр закрепляются — художник берётся за чистовую работу.</p>
            <p className="muted settings-note">
              {revisions.exhausted
                ? 'Бесплатные правки закончились — изменения по договорённости.'
                : `Изменить потом можно, но это правка: осталось ${revisions.left} из ${revisions.limit}.`}
            </p>
            {approveSketch.error && <ErrorNotice error={approveSketch.error} />}
            <Button
              disabled={approveSketch.isPending}
              onClick={() => {
                approveSketch.mutate();
                setDialog(null);
              }}
            >
              {approveSketch.isPending ? 'Согласуем…' : 'Да, всё верно'}
            </Button>
            <Button variant="secondary" onClick={() => setDialog(null)}>
              Ещё посмотрю
            </Button>
          </div>
        </Modal>
      )}

      {(dialog === 'comment' || dialog === 'revision') && (
        <MessageModal
          orderId={id}
          kind={dialog}
          onClose={() => setDialog(null)}
          onDone={() => {
            invalidate();
            void queryClient.invalidateQueries({ queryKey: ['customer', 'order', id] });
            setDialog(null);
          }}
        />
      )}
    </>
  );
}

function MessageModal({
  orderId,
  kind,
  onClose,
  onDone,
}: {
  orderId: string;
  kind: 'comment' | 'revision';
  onClose: () => void;
  onDone: () => void;
}) {
  const [body, setBody] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      api(`/customer/orders/${orderId}/${kind === 'revision' ? 'revisions' : 'comments'}`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={kind === 'revision' ? 'Запросить правки' : 'Написать художнику'}
      onClose={onClose}
    >
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field
          label="Сообщение"
          hint={
            kind === 'revision'
              ? 'Опишите, что нужно изменить. Заказ перейдёт в состояние ожидания правок.'
              : undefined
          }
        >
          <textarea
            className="input input--textarea"
            rows={5}
            required
            maxLength={2000}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </Field>
        {mutation.error && <ErrorNotice error={mutation.error} />}
        <Button type="submit" wide disabled={!body.trim() || mutation.isPending}>
          {mutation.isPending ? 'Отправляем…' : 'Отправить'}
        </Button>
      </form>
    </Modal>
  );
}
